<template>
  <section class="hero-section overflow-hidden text-slate-950 dark:text-white">
    <div class="mx-auto max-w-6xl px-4 sm:px-6">
      <div class="hero-top pt-36 md:pt-44">
        <div class="mx-auto flex max-w-4xl flex-col items-center text-center">
          <h1 class="hero-title mt-5 max-w-4xl break-words text-4xl font-extrabold leading-[1.02] tracking-normal text-slate-950 dark:text-white sm:text-5xl lg:text-7xl">
            {{ heroCopy.title }}
          </h1>

          <p class="hero-sub mt-6 max-w-2xl text-base leading-7 tracking-normal text-slate-600 dark:text-slate-300 sm:text-lg">
            {{ heroCopy.description }}
          </p>

          <div class="hero-actions mt-8 flex w-full flex-col justify-center gap-3 sm:w-auto sm:flex-row">
            <a
              class="inline-flex min-h-12 items-center justify-center rounded-md bg-slate-950 px-6 text-sm font-semibold text-white shadow-lg shadow-slate-950/10 transition-colors hover:bg-slate-800 dark:bg-slate-50 dark:text-slate-900 dark:shadow-none dark:hover:bg-white"
              :href="handleReferral('https://app.workspace369.com/signup')"
              data-conversion-event="start_free_click"
              data-conversion-label="Home hero primary CTA"
              data-conversion-location="home-hero"
            >
              {{ heroCopy.primaryCta }}
            </a>
            <NuxtLink
              class="inline-flex min-h-12 items-center justify-center rounded-md border border-slate-200 bg-white/80 px-6 text-sm font-semibold text-slate-950 shadow-sm transition-colors hover:border-slate-300 hover:bg-white dark:border-slate-700 dark:bg-white/5 dark:text-white dark:hover:border-slate-500 dark:hover:bg-white/10"
              :to="localePath('/contact-sales')"
              data-conversion-event="demo_request_click"
              data-conversion-label="Home hero secondary CTA"
              data-conversion-location="home-hero"
            >
              {{ heroCopy.secondaryCta }}
            </NuxtLink>
          </div>
          <TrialDisclosure centered />
          <!-- Last in the HTML so crawlers read the H1 and opener first; order-first keeps it above the H1 for powered_by visitors. -->
          <PoweredByReferralStrip class="order-first" />
        </div>

        <div class="hero-preview mt-10 min-w-0 md:mt-12">
          <div
            class="hero-capabilities"
            :class="{ 'is-auto': autoplay }"
            role="tablist"
            :aria-label="tr('Workspace369 core capabilities')"
          >
            <button
              v-for="(item, index) in carouselItems"
              :key="item.label"
              type="button"
              role="tab"
              class="hero-capability"
              :class="{ 'is-active': activeIndex === index }"
              :aria-selected="activeIndex === index"
              :id="`hero-tab-${index}`"
              :tabindex="activeIndex === index ? 0 : -1"
              aria-controls="hero-panel"
              @keydown="autoplay = false; navigateTabs($event, index, carouselItems.length, selectCarouselItem)"
              @click="autoplay = false; selectCarouselItem(index)"
            >
              <span>{{ item.label }}</span>
              <i
                v-if="autoplay && activeIndex === index"
                aria-hidden="true"
                :style="{ transform: `scaleX(${carouselProgress})` }"
              ></i>
            </button>
          </div>

          <div
            ref="stageEl"
            class="hero-premium-stage"
            :aria-label="tr('Workspace369 product preview')"
            @pointerenter="hovering = true"
            @pointerleave="hovering = false"
            @focusin="hovering = true"
            @focusout="hovering = false"
          >
            <HeroBackdrop />
            <div class="hero-video-wash" aria-hidden="true"></div>

            <div class="hero-screen-frame" id="hero-panel" role="tabpanel" :aria-labelledby="`hero-tab-${activeIndex}`" tabindex="0">
              <div class="hero-window-bar" aria-hidden="true">
                <span></span>
                <span></span>
                <span></span>
                <strong>{{ activeItem.windowLabel }}</strong>
              </div>
              <div
                v-if="activeItem.featureGroups"
                :key="activeItem.label"
                ref="flRoot"
                class="hero-feature-library"
              >
                <div class="fl-wall" aria-hidden="true">
                  <div v-for="(band, bi) in flWallBands" :key="bi" class="fl-band">
                    <div
                      v-for="(row, ri) in band"
                      :key="ri"
                      class="fl-row"
                      :class="{ reverse: (bi * 2 + ri) % 2 === 1 }"
                      :style="{ '--fl-speed': `${48 + (bi * 2 + ri) * 7}s` }"
                    >
                      <div class="fl-track">
                        <span
                          v-for="(tile, ti) in [...row, ...row]"
                          :key="ti"
                          class="fl-tile"
                          :class="{
                            on: tile.group === flGroup && flPhase !== 'clear',
                            sel: tile.group === flGroup && tile.index === flActive && flPhase !== 'type' && flPhase !== 'clear',
                          }"
                          :style="{ '--fl-c': tile.color }"
                        >
                          <span class="mi">{{ tile.icon }}</span>{{ tile.name }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="fl-center">
                  <strong class="fl-title">{{ heroCopy.featureLibrary.title }}</strong>

                  <div class="fl-palette" aria-hidden="true">
                    <div class="fl-input">
                      <span class="mi">search</span>
                      <span class="fl-typed">{{ flTyped }}<i class="fl-caret"></i></span>
                      <span class="fl-kbd">⌘K</span>
                    </div>
                    <div class="fl-results" :style="{ '--fl-shift': flShift }">
                      <div class="fl-results-head">
                        <span>{{ flCurrent?.label }}</span>
                        <em>{{ flCurrent?.features.length || 0 }}</em>
                      </div>
                      <div class="fl-list">
                        <div class="fl-empty" :class="{ on: flRevealed === 0 }">
                          <span class="mi">manage_search</span>
                          <span>{{ flGroups.map((g) => g.label).join(" · ") }}</span>
                        </div>
                        <div class="fl-list-track">
                          <div
                            v-for="(item, ii) in flCurrentItems"
                            :key="`${flGroup}-${item.name}`"
                            class="fl-item"
                            :class="{
                              shown: flRevealed > ii,
                              active: ii === flActive && flPhase !== 'type',
                            }"
                            :style="{ '--fl-c': item.color, '--fl-i': ii }"
                          >
                            <span class="fl-item-ic"><span class="mi">{{ item.icon }}</span></span>
                            <span class="fl-item-name">{{ item.name }}</span>
                            <span class="fl-item-enter">↵</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="fl-foot">
                      <span><i>↑</i><i>↓</i></span>
                      <span><i>↵</i></span>
                      <span class="fl-foot-count">{{ flActive + 1 }}/{{ flCurrentItems.length }}</span>
                    </div>
                  </div>
                </div>

              </div>
              <ProductScene
                v-else
                ref="productScene"
                :scene="labScenes[activeItem.id] || 'dashboard'"
                :compact="activeItem.id === 'ai' || activeItem.id === 'inbox'"
                class="hero-lab-host"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { productIconFontUrl } from "~/data/product-icon-font";
import { navigateTabs } from "~/utils/tabNavigation";
const { addReferralToUrl } = useReferral();
const { localePath } = useWorkspaceLocale();
const homeCopy = useHomeCopy();
const { tr } = useHomeI18n();

const activeIndex = ref(0);

// Preload only the compact site subset, not the complete Material icon catalog.
useHead({ link: [{ rel: 'preload', as: 'font', type: 'font/woff2', crossorigin: '', href: productIconFontUrl }] });

const heroCopy = computed(() => homeCopy.value.hero);
const featureGroups = computed(() => heroCopy.value.featureGroups);

const carouselItems = computed(() => [
  { id: "dashboard", label: heroCopy.value.tabs.dashboard, windowLabel: heroCopy.value.windows.dashboard },
  { id: "crm", label: heroCopy.value.tabs.crm, windowLabel: heroCopy.value.windows.crm },
  { id: "ai", label: heroCopy.value.tabs.ai, windowLabel: heroCopy.value.windows.ai },
  { id: "projects", label: heroCopy.value.tabs.projects, windowLabel: heroCopy.value.windows.projects },
  { id: "inbox", label: heroCopy.value.tabs.inbox, windowLabel: heroCopy.value.windows.inbox },
  { id: "invoices", label: heroCopy.value.tabs.invoices, windowLabel: heroCopy.value.windows.invoices },
  {
    id: "more",
    label: heroCopy.value.tabs.more,
    windowLabel: heroCopy.value.windows.library,
    featureGroups: featureGroups.value,
  },
]);

const activeItem = computed(() => carouselItems.value[activeIndex.value] || carouselItems.value[0]);

// --- auto-rotation (desktop and tablet, as the original hero did) ---
// Each tab stays up long enough for its own story: the Workflows run takes
// ~14s, the Inbox walk-through ~12s. Hovering the stage pauses; the timer
// also holds while the hero is offscreen or the tab is hidden.
const tabDurations = { dashboard: 7000, crm: 7000, ai: 16500, projects: 7000, inbox: 13000, invoices: 7000, more: 12000 };
const TICK = 100;
const stageEl = ref(null);
const autoplay = ref(false);
const hovering = ref(false);
const heroVisible = ref(true);
const carouselElapsed = ref(0);
const carouselProgress = computed(() =>
  Math.min(1, carouselElapsed.value / (tabDurations[activeItem.value.id] || 7000)),
);
let carouselTimer = null;
let carouselMedia = null;
let heroObserver = null;

const selectCarouselItem = (index) => {
  activeIndex.value = index;
  carouselElapsed.value = 0;
};

const carouselTick = () => {
  if (!autoplay.value || hovering.value || !heroVisible.value || document.hidden) return;
  carouselElapsed.value += TICK;
  if (carouselProgress.value >= 1) {
    selectCarouselItem((activeIndex.value + 1) % carouselItems.value.length);
  }
};

const syncAutoplay = () => {
  autoplay.value = Boolean(carouselMedia?.matches);
};

onMounted(() => {
  carouselMedia = window.matchMedia("(min-width: 641px) and (prefers-reduced-motion: no-preference)");
  syncAutoplay();
  carouselMedia.addEventListener?.("change", syncAutoplay);
  carouselTimer = window.setInterval(carouselTick, TICK);
  if (stageEl.value && "IntersectionObserver" in window) {
    heroObserver = new IntersectionObserver(([entry]) => {
      heroVisible.value = entry.isIntersecting;
    }, { threshold: 0.35 });
    heroObserver.observe(stageEl.value);
  }
});

const handleReferral = (baseUrl) => addReferralToUrl(baseUrl);

const labScenes = {
  dashboard: "dashboard",
  crm: "clients",
  projects: "projects",
  inbox: "inbox",
  invoices: "invoices",
  ai: "workflows",
};
const productScene = ref(null);

watch(activeIndex, () => {
  const sceneId = labScenes[activeItem.value.id];
  if (sceneId) productScene.value?.setScene(sceneId);
});

// --- feature library: ⌘K palette over a moving wall of every feature.
// The palette types a group name, its results drop in, the selection walks
// them while the matching tiles in the wall light up, then it clears and
// moves to the next group. Driven by a watch (not mount) because Hero never
// unmounts across tab switches.
const flRoot = ref(null);
const flGroups = computed(() => activeItem.value?.featureGroups || []);
const flGroup = ref(0);
const flActive = ref(0);
const flRevealed = ref(0);
const flTyped = ref("");
const flPhase = ref("type");

const flPalette = ["#1BA7CE", "#8E4EC6", "#30A46C", "#F76A15", "#3E63DD", "#D6409F"];
const flGroupIcons = ["work_outline", "event", "receipt_long", "trending_up", "api", "apps"];
// English feature names → Material icon. Localized names fall back to the
// group icon, so every locale still gets a complete wall.
const flIcons = {
  "Time tracking": "timer",
  "Task boards": "view_kanban",
  Habits: "repeat",
  Notes: "sticky_note_2",
  Productivity: "insights",
  "Dashboard customization": "dashboard_customize",
  "Calendar & scheduling": "calendar_month",
  "Online booking": "event_available",
  "Client portals": "web",
  "Team management": "groups",
  "Multi-tenant workspaces": "domain",
  Expenses: "payments",
  Accounting: "account_balance",
  "Product catalog": "category",
  "Inventory tracking": "inventory_2",
  "Split payments": "call_split",
  "Purchase order matching": "fact_check",
  Automations: "account_tree",
  Campaigns: "campaign",
  "Advanced proposal creator": "description",
  "PDF customization": "picture_as_pdf",
  "Theme management": "palette",
  "Outbound APIs": "north_east",
  "Inbound APIs": "south_west",
  "API integrations": "hub",
  "Audit trails": "history",
  "SharePoint file versioning": "difference",
  "SharePoint auditing": "policy",
};

const flItems = computed(() =>
  flGroups.value.map((group, gi) =>
    group.features.map((name, index) => ({
      name,
      index,
      group: gi,
      color: flPalette[gi % flPalette.length],
      icon: flIcons[name] || flGroupIcons[gi % flGroupIcons.length],
    })),
  ),
);
const flCurrent = computed(() => flGroups.value[flGroup.value]);
const flCurrentItems = computed(() => flItems.value[flGroup.value] || []);
// Phones show four result rows; keep the selected one in view.
const flShift = computed(() => Math.max(0, flActive.value - 3));

// Deal every feature round-robin into four rows so each row mixes groups.
const flWallRows = computed(() => {
  const all = flItems.value.flat();
  const rows = [[], [], [], []];
  all.forEach((tile, i) => rows[i % rows.length].push(tile));
  return rows.filter((row) => row.length);
});
// Two rows above the title, two along the bottom (under the palette's edge).
const flWallBands = computed(() => [flWallRows.value.slice(0, 2), flWallRows.value.slice(2)]);

let flTimer = null;
let flObserver = null;
let flTick = 0;

const flReset = (group = 0) => {
  flGroup.value = group;
  flActive.value = 0;
  flRevealed.value = 0;
  flTyped.value = "";
  flPhase.value = "type";
  flTick = 0;
};

const flStep = () => {
  if (document.hidden) return;
  const groups = flGroups.value;
  if (!groups.length) return;
  const group = groups[flGroup.value % groups.length];
  flTick += 1;
  switch (flPhase.value) {
    case "type":
      flTyped.value = group.label.slice(0, flTyped.value.length + 1);
      if (flTyped.value.length >= 3) flRevealed.value = Math.min(group.features.length, flRevealed.value + 1);
      if (flTyped.value.length >= group.label.length) {
        flPhase.value = "reveal";
        flTick = 0;
      }
      break;
    case "reveal":
      flRevealed.value = Math.min(group.features.length, flRevealed.value + 1);
      if (flRevealed.value >= group.features.length) {
        flPhase.value = "walk";
        flTick = 0;
      }
      break;
    case "walk":
      if (flTick % 6 === 0) {
        if (flActive.value < group.features.length - 1) {
          flActive.value += 1;
        } else {
          flPhase.value = "hold";
          flTick = 0;
        }
      }
      break;
    case "hold":
      if (flTick >= 10) {
        flPhase.value = "clear";
        flTick = 0;
      }
      break;
    default:
      flTyped.value = flTyped.value.slice(0, -2);
      flRevealed.value = Math.max(0, flRevealed.value - 2);
      if (!flTyped.value) flReset((flGroup.value + 1) % groups.length);
  }
};

const stopFl = () => {
  if (flTimer) {
    window.clearInterval(flTimer);
    flTimer = null;
  }
  flObserver?.disconnect();
  flObserver = null;
};

watch(
  () => Boolean(activeItem.value?.featureGroups),
  async (isLibrary) => {
    stopFl();
    if (!isLibrary) return;
    flReset();
    await nextTick();
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      const first = flGroups.value[0];
      flTyped.value = first?.label || "";
      flRevealed.value = first?.features.length || 0;
      flPhase.value = "hold";
      return;
    }
    if (flRoot.value && "IntersectionObserver" in window) {
      flObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !flTimer) {
            flTimer = window.setInterval(flStep, 95);
          } else if (!entry.isIntersecting && flTimer) {
            window.clearInterval(flTimer);
            flTimer = null;
          }
        },
        { threshold: 0.2 },
      );
      flObserver.observe(flRoot.value);
    } else {
      flTimer = window.setInterval(flStep, 95);
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  stopFl();
  window.clearInterval(carouselTimer);
  carouselMedia?.removeEventListener?.("change", syncAutoplay);
  heroObserver?.disconnect();
});
</script>

<style scoped>
.hero-preview {
  position: relative;
  width: 100%;
}

.hero-section {
  position: relative;
  background:
    linear-gradient(180deg, #ffffff 0%, #f9fdff 42%, #eefaff 100%);
}

.dark .hero-section {
  background:
    radial-gradient(circle at 50% 13%, rgb(27 167 206 / 0.2), transparent 32rem),
    linear-gradient(180deg, #020617 0%, #07111f 48%, #0f172a 100%);
}

.hero-section::before {
  position: absolute;
  inset: 0 0 auto;
  height: 18rem;
  background: linear-gradient(180deg, rgb(14 165 233 / 0.08), transparent);
  content: "";
  pointer-events: none;
}

.dark .hero-section::before {
  background: linear-gradient(180deg, rgb(27 167 206 / 0.12), transparent);
}

.hero-premium-stage {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  width: 100vw;
  min-height: clamp(31rem, 72vw, 52rem);
  /* Both sides: an over-wide block keeps only its start margin, which is the right one on RTL editions. */
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  padding: clamp(0.85rem, 1.8vw, 1.35rem) 1rem clamp(2.25rem, 4.8vw, 4rem);
}

.hero-bg-video,
.hero-video-wash {
  position: absolute;
  left: 0;
  width: 100%;
}

.hero-bg-video {
  top: 0;
  z-index: -3;
  height: 100%;
  object-fit: fill;
  object-position: center bottom;
  opacity: 1;
  filter: saturate(1.35) contrast(1.05);
}

.hero-video-wash {
  inset: 0;
  z-index: -2;
  background:
    linear-gradient(180deg, #f9fdff 0%, rgb(249 253 255 / 0.38) 22%, rgb(249 253 255 / 0.04) 48%, transparent 100%),
    radial-gradient(circle at 50% 36%, rgb(255 255 255 / 0.02), transparent 72%);
}

.dark .hero-video-wash {
  background:
    linear-gradient(180deg, #07111f 0%, rgb(7 17 31 / 0.76) 24%, rgb(7 17 31 / 0.16) 56%, rgb(15 23 42 / 0.18) 100%),
    radial-gradient(circle at 50% 38%, rgb(27 167 206 / 0.08), transparent 70%);
}

.hero-screen-frame {
  position: relative;
  overflow: hidden;
  width: min(74rem, calc(100vw - 2rem));
  margin: 0 auto;
  border: 1px solid rgb(15 23 42 / 0.12);
  border-radius: 10px;
  background: rgb(255 255 255 / 0.52);
  box-shadow: 0 34px 110px rgb(15 23 42 / 0.24), 0 1px 0 rgb(255 255 255 / 0.9) inset;
  -webkit-backdrop-filter: blur(14px);
  backdrop-filter: blur(14px);
}

.dark .hero-screen-frame {
  border-color: rgb(148 163 184 / 0.18);
  background: rgb(15 23 42 / 0.66);
  box-shadow: 0 34px 110px rgb(0 0 0 / 0.42), 0 1px 0 rgb(255 255 255 / 0.08) inset;
}

/* Every slide shares one box (33:20, never shorter than the replica's
   320px floor) so switching tabs never changes the page height. The
   explicit width matters: with width:auto, aspect-ratio turns the 320px
   min-height into a 528px min-width and phones cropped every scene. */
.hero-lab-host,
.hero-feature-library {
  width: 100%;
  min-width: 0;
  aspect-ratio: 33 / 20;
  min-height: 320px;
}

.hero-lab-host {
  background: #f7f7f7;
}

/* Let the replica shrink to the host on phones so its container queries
   switch scenes to their narrow layouts instead of cropping at 596px. */
.hero-lab-host :deep(.replica) {
  width: 100%;
  min-width: 0;
}

.dark .hero-lab-host {
  background: #111;
}

/* ---------- +99 more features: palette over a feature wall ---------- */

.hero-feature-library {
  --fl-bg: #f6f8fa;
  --fl-surface: #ffffff;
  --fl-line: #e3e6ea;
  --fl-text: #171717;
  --fl-muted: #7b8490;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  background: var(--fl-bg);
  padding: clamp(1.1rem, 2.6vw, 2.1rem) clamp(0.9rem, 2vw, 1.5rem) clamp(0.9rem, 1.8vw, 1.3rem);
  text-align: left;
}

.dark .hero-feature-library {
  --fl-bg: #0b1220;
  --fl-surface: #111a2b;
  --fl-line: #223047;
  --fl-text: #f1f5f9;
  --fl-muted: #8b9ab0;
}

.hero-feature-library .mi {
  font-family: "Material Icons Outlined";
  font-size: 1.05em;
  font-style: normal;
  font-weight: normal;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  white-space: nowrap;
  direction: ltr;
  -webkit-font-feature-settings: "liga";
  font-feature-settings: "liga";
}

.fl-wall {
  position: absolute;
  inset: 0;
  z-index: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 1rem 0;
  pointer-events: none;
  -webkit-mask-image: linear-gradient(90deg, transparent, #000 9%, #000 91%, transparent);
  mask-image: linear-gradient(90deg, transparent, #000 9%, #000 91%, transparent);
}

.fl-band {
  display: grid;
  gap: 0.6rem;
}

.fl-row {
  overflow: hidden;
}

.fl-track {
  display: flex;
  width: max-content;
  gap: 0.6rem;
  animation: flMarquee var(--fl-speed, 50s) linear infinite;
}

.fl-row.reverse .fl-track {
  animation-direction: reverse;
}

@keyframes flMarquee {
  from {
    transform: translateX(0);
  }

  to {
    transform: translateX(calc(-50% - 0.3rem));
  }
}

.fl-tile {
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid var(--fl-line);
  border-radius: 0.6rem;
  background: var(--fl-surface);
  color: var(--fl-muted);
  font-size: 0.78rem;
  font-weight: 500;
  line-height: 1;
  padding: 0.55rem 0.8rem 0.55rem 0.6rem;
  transition:
    color 0.35s,
    border-color 0.35s,
    box-shadow 0.35s;
}

.fl-tile .mi {
  font-size: 1.05rem;
  color: #a3abb6;
  transition: color 0.35s;
}

.fl-tile.on {
  border-color: color-mix(in srgb, var(--fl-c) 45%, transparent);
  color: var(--fl-text);
  box-shadow: 0 6px 18px color-mix(in srgb, var(--fl-c) 16%, transparent);
}

.fl-tile.on .mi {
  color: var(--fl-c);
}

.fl-tile.sel {
  border-color: var(--fl-c);
  box-shadow:
    0 0 0 3px color-mix(in srgb, var(--fl-c) 18%, transparent),
    0 8px 22px color-mix(in srgb, var(--fl-c) 22%, transparent);
}

.fl-center {
  position: relative;
  z-index: 1;
  display: flex;
  flex: 1;
  min-height: 0;
  width: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding-top: clamp(5.4rem, 8.6vw, 6.6rem);
}

.fl-title {
  display: block;
  max-width: 34rem;
  color: var(--fl-text);
  font-size: clamp(1.35rem, 2.7vw, 2.35rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.08;
  text-align: center;
}

.fl-palette {
  width: min(34rem, 100%);
  margin-top: clamp(0.9rem, 2vw, 1.5rem);
  overflow: hidden;
  border: 1px solid var(--fl-line);
  border-radius: 10px;
  background: var(--fl-surface);
  box-shadow:
    0 30px 70px rgb(15 23 42 / 0.18),
    0 4px 14px rgb(15 23 42 / 0.06);
}

.dark .fl-palette {
  box-shadow: 0 30px 80px rgb(0 0 0 / 0.5);
}

.fl-input {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  border-bottom: 1px solid var(--fl-line);
  padding: 0.8rem 0.95rem;
}

.fl-input > .mi {
  color: var(--fl-muted);
  font-size: 1.15rem;
}

.fl-typed {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  color: var(--fl-text);
  font-size: 0.95rem;
  font-weight: 500;
  white-space: nowrap;
}

.fl-caret {
  display: inline-block;
  width: 2px;
  height: 1.05em;
  margin-left: 1px;
  background: #1ba7ce;
  vertical-align: -0.18em;
  animation: flBlink 1s step-end infinite;
}

@keyframes flBlink {
  50% {
    opacity: 0;
  }
}

.fl-kbd,
.fl-foot i {
  flex: none;
  border: 1px solid var(--fl-line);
  border-radius: 0.3rem;
  color: var(--fl-muted);
  font-size: 0.66rem;
  font-style: normal;
  font-weight: 600;
  line-height: 1;
  padding: 0.22rem 0.36rem;
}

.fl-results {
  --fl-row: 2.55rem;
  padding: 0.45rem 0.45rem 0.4rem;
}

.fl-results-head {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  color: var(--fl-muted);
  font-size: 0.68rem;
  font-weight: 600;
  padding: 0.2rem 0.5rem 0.4rem;
}

.fl-results-head em {
  border-radius: 6px;
  background: color-mix(in srgb, var(--fl-muted) 14%, transparent);
  font-style: normal;
  padding: 0.12rem 0.42rem;
}

.fl-list {
  position: relative;
  height: calc(var(--fl-row) * 6);
  overflow: hidden;
}

.fl-empty {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: var(--fl-muted);
  font-size: 0.78rem;
  opacity: 0;
  padding: 0 1.5rem;
  text-align: center;
  transition: opacity 0.25s;
}

.fl-empty.on {
  opacity: 1;
}

.fl-empty .mi {
  color: #0e7490;
  font-size: 1.6rem;
}

.fl-list-track {
  transition: transform 0.35s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.fl-item {
  display: flex;
  height: var(--fl-row);
  align-items: center;
  gap: 0.7rem;
  border-radius: 0.55rem;
  color: var(--fl-text);
  font-size: 0.85rem;
  font-weight: 500;
  opacity: 0;
  padding: 0 0.5rem;
  transform: translateY(6px);
  transition:
    opacity 0.28s ease,
    transform 0.28s ease,
    background 0.2s;
}

.fl-item.shown {
  opacity: 1;
  transform: none;
}

.fl-item.active {
  background: color-mix(in srgb, var(--fl-c) 10%, transparent);
}

.fl-item-ic {
  display: grid;
  width: 1.75rem;
  height: 1.75rem;
  flex: none;
  place-items: center;
  border-radius: 0.45rem;
  background: color-mix(in srgb, var(--fl-c) 13%, transparent);
  color: var(--fl-c);
}

.fl-item-ic .mi {
  font-size: 1.05rem;
}

.fl-item-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fl-item-enter {
  width: 1.2rem;
  flex: none;
  color: var(--fl-c);
  font-size: 0.8rem;
  opacity: 0;
  text-align: center;
}

.fl-item.active .fl-item-enter {
  opacity: 1;
}

.fl-foot {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  border-top: 1px solid var(--fl-line);
  padding: 0.55rem 0.95rem;
}

.fl-foot span {
  display: inline-flex;
  gap: 0.25rem;
}

.fl-foot .fl-foot-count {
  margin-left: auto;
  color: var(--fl-muted);
  font-size: 0.7rem;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}

@media (max-width: 900px) {
  .fl-list {
    height: calc(var(--fl-row) * 4);
  }

  .fl-list-track {
    transform: translateY(calc(var(--fl-shift) * var(--fl-row) * -1));
  }
}

@media (max-width: 640px) {
  .hero-feature-library {
    justify-content: center;
    padding: 0.75rem 0.6rem 0.65rem;
  }

  .fl-wall {
    justify-content: flex-start;
    padding: 0.6rem 0;
  }

  .fl-band {
    gap: 0.45rem;
  }

  .fl-band + .fl-band {
    display: none;
  }

  .fl-tile {
    font-size: 0.68rem;
    padding: 0.45rem 0.65rem 0.45rem 0.5rem;
  }

  .fl-center {
    justify-content: center;
    padding-top: 4.6rem;
  }

  .fl-title {
    font-size: 1.3rem;
    max-width: 17rem;
  }

  .fl-palette {
    margin-top: 0.6rem;
    border-radius: 10px;
  }

  .fl-input {
    padding: 0.6rem 0.75rem;
  }

  .fl-typed {
    font-size: 0.82rem;
  }

  .fl-results {
    --fl-row: 2.15rem;
    padding: 0.3rem;
  }

  .fl-results-head {
    display: none;
  }

  .fl-list {
    height: calc(var(--fl-row) * 4);
  }

  .fl-item {
    font-size: 0.76rem;
  }

  .fl-item-ic {
    width: 1.5rem;
    height: 1.5rem;
  }

  .fl-foot {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .fl-track,
  .fl-caret {
    animation: none;
  }

  .fl-tile,
  .fl-item,
  .fl-list-track {
    transition: none;
  }
}

.hero-window-bar {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  border-bottom: 1px solid rgb(15 23 42 / 0.1);
  background: rgb(255 255 255 / 0.72);
  padding: 0.75rem 0.9rem;
}

.dark .hero-window-bar {
  border-color: rgb(148 163 184 / 0.16);
  background: rgb(2 6 23 / 0.68);
}

.hero-window-bar span {
  height: 0.55rem;
  width: 0.55rem;
  border-radius: 999px;
  background: rgb(100 116 139 / 0.42);
}

.hero-window-bar strong {
  margin-left: 0.35rem;
  color: #334155;
  font-size: 0.72rem;
  font-weight: 850;
  letter-spacing: 0;
}

.dark .hero-window-bar strong {
  color: #cbd5e1;
}

.hero-capabilities {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 0.72rem;
  /* Pulls the row up by the tabs' extra top padding, so the labels and bars stay where they were. */
  margin: -1.4rem auto 0.45rem;
  max-width: 68rem;
}

.hero-capability {
  position: relative;
  min-width: 0;
  overflow: hidden;
  /* The top padding makes each tab a 44px tap target (it was 28px at 125% text). */
  padding: 1.4rem 0 0.52rem;
  text-align: left;
  color: #526171;
  font-size: 0.72rem;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: 0;
  transition: color 180ms ease;
}

.dark .hero-capability {
  color: #cbd5e1;
}

.hero-capability::after {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 2px;
  border-radius: 999px;
  background: rgb(148 163 184 / 0.32);
  content: "";
  transition: background 180ms ease;
}

.dark .hero-capability::after {
  background: rgb(148 163 184 / 0.24);
}

.hero-capability span {
  display: inline-block;
}

.hero-capability i {
  position: absolute;
  z-index: 1;
  right: 0;
  bottom: -0.5px;
  left: 0;
  height: 3px;
  border-radius: 999px;
  background: #0ea5e9;
  transform-origin: left center;
  transition: transform 100ms linear;
}

.hero-capability.is-active {
  color: #0f172a;
}

.hero-capability.is-active::after {
  background: #0ea5e9;
}

/* While rotating, the active tab shows a track that the progress bar fills. */
.is-auto .hero-capability.is-active::after {
  background: rgb(148 163 184 / 0.32);
}

.dark .hero-capability.is-active {
  color: #ffffff;
}

.hero-capability:focus-visible {
  border-radius: 0.35rem;
  outline: 2px solid rgb(14 165 233 / 0.55);
  outline-offset: 3px;
}

@media (prefers-reduced-motion: reduce) {
  .hero-bg-video {
    display: none;
  }
}

@media (max-width: 900px) {
  .hero-capabilities {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.64rem 0.78rem;
  }
}

@media (max-width: 640px) {
  /* First screen on a phone: headline, one line of buttons, then the live product
     (it used to start at 784px, below the fold on most phones). */
  .hero-top {
    padding-top: 7.4rem;
  }

  .hero-title {
    margin-top: 0;
    /* A centred flex item is as wide as its longest word, so "Kundengeschäft" (de) or
       "klantwerkbedrijf" (nl) pushed the H1 past the screen at 125% text. Hold it to the
       column and hyphenate the long words. */
    width: 100%;
  }

  .hero-title:not(:lang(en)) {
    -webkit-hyphens: auto;
    hyphens: auto;
    /* Only split words of 10+ letters, keeping 4 on each side: Safari and Chrome otherwise
       split short words too ("ei-nem Ort", "yh-dessä"). */
    hyphenate-limit-chars: 10 4 4;
    -webkit-hyphenate-limit-before: 4;
    -webkit-hyphenate-limit-after: 4;
  }

  .hero-sub {
    margin-top: 0.9rem;
    font-size: 0.95rem;
    line-height: 1.55;
  }

  .hero-actions {
    display: grid;
    grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr);
    gap: 0.6rem;
    margin-top: 1.25rem;
  }

  .hero-actions > * {
    min-height: 2.9rem;
    padding: 0 0.75rem;
  }

  .hero-preview {
    margin-top: 1.4rem;
  }

  /* Tabs: one swipeable row instead of a four-row grid. */
  .hero-capabilities {
    display: flex;
    gap: 1.1rem;
    margin: -1.4rem -1rem 0.45rem;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    padding: 0 1rem;
    scroll-padding-inline: 1rem;
    scroll-snap-type: x proximity;
    scrollbar-width: none;
  }

  .hero-capabilities::-webkit-scrollbar {
    display: none;
  }

  .hero-capability {
    flex: none;
    font-size: 0.78rem;
    scroll-snap-align: start;
    white-space: nowrap;
  }

  .hero-premium-stage {
    min-height: 0;
    padding: 0.55rem 0.25rem 2.4rem;
  }

  /* Phones get a taller fixed box so the narrow scene layouts show real
     rows instead of a 320px sliver. Same box for every tab. */
  .hero-lab-host,
  .hero-feature-library {
    aspect-ratio: auto;
    height: 29rem;
  }

  .hero-bg-video {
    object-fit: cover;
  }

  .hero-screen-frame {
    border-radius: 10px;
    width: calc(100vw - 1rem);
  }
}
</style>
